export const asyncAction = (type: string) => ({
  PENDING: `${type}_PENDING`,
  SUCCESS: `${type}_SUCCESS`,
  FAILURE: `${type}_FAILURE`,
});

// Phase 2 — vehicle-share unread count. Plain action types because
// these don't go over the network; they're optimistic UI bumps from
// the socket listener and a manual clear when the user opens the
// share-notifications screen.
export const BUMP_SHARE_UNREAD = 'BUMP_SHARE_UNREAD';
export const CLEAR_SHARE_UNREAD = 'CLEAR_SHARE_UNREAD';

export const API_CALL = 'API_CALL';
export const FETCH_OTP = asyncAction('FETCH_OTP');
export const LOGIN = asyncAction('LOGIN');
export const LOGOUT = asyncAction('LOGOUT');
export const LOGIN_DETAILS = 'LOGIN_DETAILS';
export const LOGIN_VERIFY_STEP = 'LOGIN_VERIFY_STEP';
export const LOGIN_ATTEMPTS = asyncAction('LOGIN_ATTEMPTS');

export const REGISTER_CHECK = asyncAction('REGISTER_CHECK');
export const REGISTER = asyncAction('REGISTER');
export const REFRESH_TOKEN = asyncAction('REFRESH_TOKEN');
export const REGISTER_VERIFY_STEP = 'REGISTER_VERIFY_STEP';

export const RESEND_OTP = asyncAction('RESEND_OTP');
export const CLEAR_LOGIN_DATA = 'CLEAR_LOGIN_DATA';
export const GLOBAL_DATA = 'GLOBAL_DATA';

//Dashboard
export const DRIVER_DASHBOARD = asyncAction('DRIVER_DASHBOARD');
export const RIDER_DASHBOARD = asyncAction('RIDER_DASHBOARD');
export const BOOKINGS_HISTORY = asyncAction('BOOKINGS_HISTORY');
export const CURRENT_BALANCE = asyncAction('CURRENT_BALANCE');
export const AGENT_DASHBOARD = asyncAction('AGENT_DASHBOARD');
export const AGENT_BALANCE = asyncAction('AGENT_BALANCE');
export const AGENT_COMMISSION = asyncAction('AGENT_COMMISSION');
export const AGENT_PROFILE = asyncAction('AGENT_PROFILE');
export const AGENT_WITHDRAWALS = asyncAction('AGENT_WITHDRAWALS');
// Dedicated action for submitting a withdrawal so a failed POST does not
// overwrite the AGENT_WITHDRAWALS history list with an error payload.
export const AGENT_WITHDRAW_SUBMIT = asyncAction('AGENT_WITHDRAW_SUBMIT');

//User
export const USER_DETAILS = 'USER_DETAILS';
export const CURRENT_LOCATION = 'CURRENT_LOCATION';
export const PROFILE_FETCH = asyncAction('PROFILE_FETCH');
export const PROFILE_SAVE = asyncAction('PROFILE_SAVE');
export const DRIVER_STATUS = asyncAction('DRIVER_STATUS');
export const PROFILE_IMAGE = asyncAction('PROFILE_IMAGE');
export const GOOGLE_MAPS_LOADED = 'GOOGLE_MAPS_LOADED';
export const CAR_REGISTER_CHECK = asyncAction('CAR_REGISTER_CHECK');
export const CAR_REGISTER_CHECK_NULL = 'CAR_REGISTER_CHECK_NULL';
export const REFRESH_ZONE = asyncAction('REFRESH_ZONE');
export const LAST_BOOKING_ZONE_ID = asyncAction('LAST_BOOKING_ZONE_ID');
export const EMERGENCY_DETAILS = asyncAction('EMERGENCY_DETAILS');
export const GET_EMERGENCY_DETAILS = asyncAction('GET_EMERGENCY_DETAILS');
export const DELETE_EMERGENCY_DETAILS = asyncAction('DELETE_EMERGENCY_DETAILS');
export const FETCH_TRIPS_COUNT = asyncAction('FETCH_TRIPS_COUNT');
export const CUSTOMER_MIGRATE = asyncAction('CUSTOMER_MIGRATE');
// Plain string, NOT asyncAction(): this is a synchronous local reset with no
// request lifecycle. As an asyncAction it was an object, so the reducer's
// `case CLEAR_VEHICLE_DETAILS` could never match a string action.type.
export const CLEAR_VEHICLE_DETAILS = 'CLEAR_VEHICLE_DETAILS';

//Masters
export const COUNTRIES = asyncAction('COUNTRIES');
export const STATIC_PAGE = asyncAction('STATIC_PAGE');
export const ZONES = asyncAction('ZONES');
export const CAR_TYPES = asyncAction('CAR_TYPES');

//Booking
export const PICKUP = 'PICKUP';
export const DROP = 'DROP';
export const DROPS = 'DROPS';
export const BOOKING_DISTANCE = 'BOOKING_DISTANCE';
export const BOOKING_DURATION = 'BOOKING_DURATION';
export const BOOKING_SCREEN_SIZE = 'BOOKING_SCREEN_SIZE';
export const CAR_TYPE = 'CAR_TYPE';
export const ESTIMATED_PRICES = asyncAction('ESTIMATED_PRICES');
export const AVAILABLE_CARS = asyncAction('AVAILABLE_CARS');
export const BOOKING_SAVE = asyncAction('BOOKING_SAVE');
export const BOOKING_CANCEL = asyncAction('BOOKING_CANCEL');
export const SELECTED_LOCATION = 'SELECTED_LOCATION';
// export const CHECK_ZONE = asyncAction('CHECK_ZONE');
export const CHECK_ZONE = {
  PENDING: "CHECK_ZONE_PENDING",
  SUCCESS: "CHECK_ZONE_SUCCESS",
  FAILURE: "CHECK_ZONE_FAILURE",
};
export const BOOKING_DRIVER = 'BOOKING_DRIVER';
export const CURRENT_RIDE_CHECK = asyncAction('CURRENT_RIDE_CHECK');
export const NEW_CURRENT_RIDE = 'NEW_CURRENT_RIDE';
export const CANCEL_REASONS = asyncAction('CANCEL_REASONS');
export const CLEAR_BOOKING = 'CLEAR_BOOKING';
export const START_TRIP = asyncAction('START_TRIP');
export const STOP_TRIP = asyncAction('STOP_TRIP');
export const PAYMENT_CONFIRM_TRIP = asyncAction('PAYMENT_CONFIRM_TRIP');
export const CHAT_MESSAGE_SEND = 'CHAT_MESSAGE_SEND';
export const CHAT_MESSAGE_UPDATE = 'CHAT_MESSAGE_UPDATE';
export const CHAT_OPEN_STATUS = 'CHAT_OPEN_STATUS';
export const SELECT_RIDE = 'SELECT_RIDE';
export const CAR_EST_AMOUNT = 'CAR_EST_AMOUNT';
export const BOOKING_DATA_UPDATE = 'BOOKING_DATA_UPDATE';
export const RATING_SAVE = asyncAction('RATING_SAVE');
export const LATEST_RIDES = asyncAction('LATEST_RIDES');
export const BOOKING_PAYMENT_TYPE = asyncAction('BOOKING_PAYMENT_TYPE');
export const BOOKING_AMOUNTS = asyncAction('BOOKING_AMOUNTS');
export const ZONE_PAY_GATEWAYS = asyncAction('ZONE_PAY_GATEWAYS');
export const PAYMENT_LIST_DESC = asyncAction('PAYMENT_LIST_DESC');
export const REFRESH_BOOKING = asyncAction('REFRESH_BOOKING');
export const ROUTES_INFO = 'ROUTES_INFO';
export const CAR_LIST = asyncAction('CAR_LIST');
export const GUEST_SAVE = asyncAction('GUEST_SAVE');

// REQUIREMENTS.md §3 FR-1/2/3/6 — web rider parity. Same shape as
// the mobile slice's createAsyncThunk lifecycle. Server endpoints
// already exist (P3..P7) and accept the rider role for `request`,
// `decide`, and `list` calls.
// REQUIREMENTS.md FR-15 — canned-reply chat library fetch.
export const FETCH_CANNED_REPLIES = asyncAction('FETCH_CANNED_REPLIES');
// FR-10 / FR-13 #10 — live driver location pushed by soc (`bookingMessageDriverLoc`).
// Drives the moving driver marker on the rider's web map.
export const SET_DRIVER_LIVE_LOCATION = 'SET_DRIVER_LIVE_LOCATION';
// PHASE 0 (#12) — parity with mobile. soc emits `saveCurrentBookingTrack`
// (progressive route trail behind the car) and `etaUpdate` (live driver ETA).
// Mobile handled both; web listened to neither, so the web rider saw only a
// bare moving marker and a frozen ETA on the same trip.
export const SET_DRIVER_TRAIL = 'SET_DRIVER_TRAIL';
export const SET_DRIVER_ETA = 'SET_DRIVER_ETA';
// Payment method change → real-time driver notification (parity with mobile).
// SET_PAYMENT_CONFIRMATION holds the method the driver should reflect
// ("please wait" for card/applepay/etc., "Collect Payment" for cash).
// SET_PAYMENT_CONFIRMATION_SEND is the rider→driver broadcast trigger the
// Socket layer watches and emits over `paymentTypeConfirmation`.
export const SET_PAYMENT_CONFIRMATION = 'SET_PAYMENT_CONFIRMATION';
export const SET_PAYMENT_CONFIRMATION_SEND = 'SET_PAYMENT_CONFIRMATION_SEND';
// FR-13 #8 — cashless tipping POST. FR-13 #10 — shared ETA link issue.
export const APPLY_TIP = asyncAction('APPLY_TIP');
export const ISSUE_SHARE_LINK = asyncAction('ISSUE_SHARE_LINK');
export const FETCH_RECENT_RIDES = asyncAction('FETCH_RECENT_RIDES');
export const FETCH_EXTRA_CHARGES = asyncAction('FETCH_EXTRA_CHARGES');
export const DECIDE_EXTRA_CHARGE = asyncAction('DECIDE_EXTRA_CHARGE');
export const FETCH_AMENDMENTS = asyncAction('FETCH_AMENDMENTS');
export const REQUEST_AMENDMENT = asyncAction('REQUEST_AMENDMENT');
export const FETCH_ADVANCES = asyncAction('FETCH_ADVANCES');
export const DECIDE_ADVANCE = asyncAction('DECIDE_ADVANCE');
// FR-3 — rider sees the breakdown banner and a deep-link to the
// new (re-booked) booking. Plain action so the reducer can mirror
// the booking row's `previous_booking_id` for the "View new booking"
// CTA without a network round-trip.
export const SET_BREAKDOWN_LINK = 'SET_BREAKDOWN_LINK';
//Address
export const ADDRESS_LIST = asyncAction('ADDRESS_LIST');

//Payments
export const ORDO_ORDER_ID = asyncAction('ORDO_ORDER_ID');
export const ORDO_SMART_ID_CHECK = asyncAction('ORDO_SMART_ID_CHECK');
export const PAYMENT_STATUS = asyncAction('PAYMENT_STATUS');
export const CLEAR_ORDER_DATA = asyncAction('CLEAR_ORDER_DATA');
export const STRIPE_ORDER = asyncAction('STRIPE_ORDER');
export const JUDO_ORDER = asyncAction('JUDO_ORDER');
export const SUMUP_ORDER = asyncAction('SUMUP_ORDER');
export const SUMUP_STATUS = asyncAction('SUMUP_STATUS');
export const SYNAPTO_ORDER = asyncAction('SYNAPTO_ORDER');
export const SYNAPTO_CONFIG = asyncAction('SYNAPTO_CONFIG');

//Noticications
export const NOTIFICATIONS_LIST = asyncAction('NOTIFICATIONS_LIST');
export const NOTIFICATIONS_ACCEPTED = asyncAction('NOTIFICATIONS_ACCEPTED');
export const NOTIFICATIONS_REJECTED = asyncAction('NOTIFICATIONS_REJECTED');
export const NOTIFICATION_RECEIVED = 'NOTIFICATION_RECEIVED';

//Activity History
export const ACTIVITY_LIST = asyncAction('ACTIVITY_LIST');
export const ACTIVITY_DETAILS = asyncAction('ACTIVITY_DETAILS');

//Toast
export const TOAST_MESSAGE = 'TOAST_MESSAGE';

//Common
export const BREAD_CRUMB = 'BREAD_CRUMB';

//Transactions
export const TRANSACTIONS_LIST = asyncAction('TRANSACTIONS_LIST');
export const WITHDRAW_PENDING_LIST = asyncAction('WITHDRAW_PENDING_LIST');
export const WITHDRAW_UPDATE = asyncAction('WITHDRAW_UPDATE');
export const WITHDRAW_CANCEL = asyncAction('WITHDRAW_CANCEL');
export const PAYMENT_LIST = asyncAction('PAYMENT_LIST');


//support
export const TICKET_LIST = asyncAction('TICKET_LIST');
export const CREATE_TICKET = asyncAction('CREATE_TICKET');
export const TICKET_REPLY = asyncAction('TICKET_REPLY');
export const CATEGORY_LIST = asyncAction('CATEGORY_LIST');
export const QUESTION_LIST = asyncAction('QUESTION_LIST');

//settings
export const TIMEZONES = asyncAction('TIMEZONES');
export const FETCH_SETTINGS = asyncAction('FETCH_SETTINGS');
export const UPDATE_SETTINGS = asyncAction('UPDATE_SETTINGS');

//Schedule (corporate)
export const SCHEDULE_LIST = asyncAction('SCHEDULE_LIST');
export const SCHEDULE_DETAILS = asyncAction('SCHEDULE_DETAILS');
export const SCHEDULE_CANCEL = asyncAction('SCHEDULE_CANCEL');
export const SCHEDULE_PO_LIST = asyncAction('SCHEDULE_PO_LIST');

// Phase 1 scheduled rides (c_scheduled_rides table)
export const SCHEDULED_RIDES_LIST = asyncAction('SCHEDULED_RIDES_LIST');
export const SCHEDULED_RIDES_CANCEL = asyncAction('SCHEDULED_RIDES_CANCEL');
export const SCHEDULED_RIDES_UPDATE = asyncAction('SCHEDULED_RIDES_UPDATE');
// Zone check for the schedule-EDIT dialog. Deliberately NOT CHECK_ZONE, so it
// does not overwrite state.booking.zone of an in-progress booking. No reducer
// handles it — the dialog reads the checkzone result from the return value.
export const SCHEDULE_CHECK_ZONE    = asyncAction('SCHEDULE_CHECK_ZONE');

//Faqs
export const FAQ_LIST = asyncAction('FAQ_LIST');

//FavouriteAddress
export const FAVOURITE_ADDRESS_LIST = asyncAction('FAVOURITE_ADDRESS_LIST');
export const FAVOURITE_ADDRESS_DETAILS = asyncAction('FAVOURITE_ADDRESS_DETAILS');
export const FAVOURITE_ADDRESS_DELETE = asyncAction('FAVOURITE_ADDRESS_DELETE');

//PageInfo
export const PAGEINFO = asyncAction('PAGEINFO');

//DriverManagement
export const DRIVER_PROFILE_UPDATE = asyncAction('DRIVER_PROFILE_UPDATE');
export const UPLOAD_DRIVER_IMAGES = asyncAction('UPLOAD_DRIVER_IMAGES');
export const FETCH_DRIVER_DETAILS = asyncAction('FETCH_DRIVER_DETAILS');
export const DRIVER_FILES = asyncAction('DRIVER_FILES');
export const DRIVER_APPROVAL = asyncAction('DRIVER_APPROVAL');
export const VEHICLE_APPROVAL = asyncAction('VEHICLE_APPROVAL');

//uploadFile sent openai backend
export const IMAGE_PROCESS = asyncAction('IMAGE_PROCESS');

export const CHECK_DRIVER_VALIDATION = asyncAction('CHECK_DRIVER_VALIDATION');
export const SHARE_VEHICLE = asyncAction('SHARE_VEHICLE');
export const CANCEL_VEHICLE = asyncAction('CANCEL_VEHICLE');
export const ABORT_SHARE = asyncAction('ABORT_SHARE');
// QA recheck — explicit action types per share lifecycle so each
// reducer slice update is independent and the previous shared
// `ABORT_SHARE` aliasing (which only flipped `loading`) doesn't leave
// the slice silently out of sync after subscribe/unsubscribe/
// terminate calls.
export const SUBSCRIBE_VEHICLE = asyncAction('SUBSCRIBE_VEHICLE');
export const UNSUBSCRIBE_VEHICLE = asyncAction('UNSUBSCRIBE_VEHICLE');
export const TERMINATE_VEHICLE = asyncAction('TERMINATE_VEHICLE');


//Vehicles details
export const VEHICLE_LIST = asyncAction('VEHICLE_LIST');
export const VEHICLE_VIEW = asyncAction('VEHICLE_VIEW');
export const VEHICLE_DETAILS_SAVE = asyncAction('VEHICLE_DETAILS_SAVE');
export const VEHICLE_GET_DETAILS = asyncAction('VEHICLE_GET_DETAILS');
export const VEHICLE_DETAILS_UPDATE = asyncAction('VEHICLE_DETAILS_UPDATE');
export const UPLOAD_VEHICLE_FILES = asyncAction('UPLOAD_VEHICLE_FILES');
export const VEHICLE_FILES = asyncAction('VEHICLE_FILES');
export const VEHICLE_SHARE = asyncAction('VEHICLE_SHARE');
export const VEHICLE_CANCEL_SHARE = asyncAction('VEHICLE_CANCEL_SHARE');
export const SHARE_NOTIFICATIONS = asyncAction('SHARE_NOTIFICATIONS');
export const ACCEPT_VEHICLE_SHARING = asyncAction('ACCEPT_VEHICLE_SHARING');

//Call
export const CALL_POPUP = 'CALL_POPUP';
export const CALL_DATA = 'CALL_DATA';
export const INITIATE_CALL = 'INITIATE_CALL';
export const CREATE_CALL = 'CREATE_CALL';
export const RECEIVE_CALL = 'RECEIVE_CALL';
export const SET_VICI_SESSION = 'SET_VICI_SESSION';
export const CLEAR_VICI_SESSION = 'CLEAR_VICI_SESSION';
export const ANSWER_CALL = 'ANSWER_CALL';
export const ANSWER_CALL_RECEIVED = 'ANSWER_CALL_RECEIVED';
export const SEND_ICE_CANDIDATE = 'SEND_ICE_CANDIDATE';
export const RECEIVE_ICE_CANDIDATE = 'RECEIVE_ICE_CANDIDATE';
export const CLOSE_CALL = 'CLOSE_ALL';
export const END_CALL = 'END_CALL';
export const CLEAR_CALL = 'CLEAR_CALL';
export const CALL_ACKNOWLEDGE = 'CALL_ACKNOWLEDGE';
export const RECEIVE_ACKNOWLEDGE = 'RECEIVE_ACKNOWLEDGE';
export const VICI_ACKNOWLEDGE = 'VICI_ACKNOWLEDGE';
export const RECEIVE_VICI_ACKNOWLEDGE = 'RECEIVE_VICI_ACKNOWLEDGE';


// Airport Service
export const AIRPORT_SERVICE = 'AIRPORT_SERVICE';
export const FLIGHT_NUMBER = 'FLIGHT_NUMBER';
export const FLIGHT_DATETIME = 'FLIGHT_DATETIME';
export const TERMINAL = 'TERMINAL';
export const TRUNK_LUGGAGE = 'TRUNK_LUGGAGE';
export const HAND_LUGGAGE = 'HAND_LUGGAGE';